import { computed, defineComponent } from "vue";
import type { IProgressBarProps } from "./types";
import { clamp } from "@/utils/math";
import { toNumber } from "@/utils/number";
import { tv } from "tailwind-variants";
import { uref } from "@/utils/reactive/uref";
import { fract, lerp, smoothstep } from "math";
import { EOscillationType, useOscillation } from "~/composables/useOscillation";

const Clazz = tv({
  slots: {
    base: "bg-primary-500 rounded-lg h-8",
    blink: "bg-primary-500 h-full",
    rail: "bg-gray-200 rounded-lg",
  },
  variants: {
    color: {
      primary: {
        base: "bg-primary-500",
        blink: "bg-primary-500",
        rail: "bg-gray-200",
      },
      error: {
        base: "bg-red-500",
        blink: "bg-red-500",
      },
    },
    square: {
      true: { base: "rounded-none", rail: "rounded-none" },
    },
    size: {
      xs: { base: "h-2", rail: "h-2" },
      s: { base: "h-4", rail: "h-4" },
    },
    withBackground: {
      true: {
        base: "",
      },
    },
  },
});

const formatNum = (x: number): string => {
  if (fract(x) === 0) return x + "";
  if (Math.abs(x) >= 10 && fract(x) <= 0.0015 && Math.floor(x) % 10 === 0)
    return Math.floor(x) + "";
  if (Math.abs(x) <= 0.00003) return 0 + "";
  if (Math.abs(x) < 0.1) return x.toFixed(3);
  if (Math.abs(x) < 0.5) return x.toFixed(2);
  return x.toFixed(1);
};

const ProgressBar = defineComponent<IProgressBarProps>({
  name: "ProgressBar",
  setup(props) {
    const scale = computed(() => (props.scale100 ? 100 : 1));

    const percent = computed(() => {
      return clamp(toNumber(uref(props.value)) * scale.value, 0, 100.0);
    });
    const percentNormalized = computed(() => percent.value / 100.0);

    const { base, rail, blink } = Clazz({
      square: !!props.square,
      size: props.size as any,
      color: (props.variant || "primary") as any,
    });

    const opacity = computed(() =>
      typeof props.opacity === "undefined" ? 100 : uref(props.opacity),
    );

    const blinkOsc = useOscillation({
      speed: 10.0,
      type: EOscillationType.MOD,
    });
    const blinkWidth = computed(() =>
      lerp(
        percent.value,
        100.0,
        smoothstep(0.0, 0.75, blinkOsc.normalized.value),
      ),
    );
    const blinkWidthNormalized = computed(() => blinkWidth.value / 100.0);
    const blinkOpacity = computed(
      () =>
        (0.75 * Math.max(0.0, 1.0 - 0.93 * blinkWidthNormalized.value) * 100.0) /
        (1.0 + smoothstep(0.25, 1.0, percentNormalized.value)),
    );
    const blinkGlowFactorPx = computed(() =>
      lerp(1.0, 8.0, blinkWidthNormalized.value),
    );

    onMounted(() => {
      if (props.withBackground) {
        blinkOsc.start();
      }
    });

    onUnmounted(() => {
      if (props.withBackground) {
        blinkOsc.stop();
      }
    });

    return () => {
      return (
        <div
          class="relative"
          style={{
            ...(props.showPercentage
              ? {
                display: "grid",
                gridTemplateColumns: "clamp(2rem, 20%, 3.5rem) 1fr",
                gap: "0.5rem",
              }
              : {}),
          }}
        >
          {props.showPercentage && (
            <div
              class="text-gray-500 text-xs font-normal"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {`${formatNum(percent.value)}%`}
            </div>
          )}
          <div
            style={{
              position: "relative",
              opacity: `${opacity.value}%`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              class={base()}
              style={{
                width: `${percent.value}%`,
                opacity: `${opacity.value}%`,
                zIndex: 3,
              }}
            ></div>
            {props.withBackground ? (
              <div
                class={rail()}
                style={{
                  width: `100%`,
                  position: "absolute",
                  zIndex: 2,
                }}
              >
                <div
                  class={blink()}
                  style={{
                    width: `${blinkWidth.value}%`,
                    opacity: `${blinkOpacity.value}%`,
                    filter: `blur(${blinkGlowFactorPx.value}px)`,
                  }}
                ></div>
              </div>
            ) : null}
          </div>
        </div>
      );
    };
  },
});

ProgressBar.props = [
  "value",
  "type",
  "showPercentage",
  "size",
  "square",
  "opacity",
  "scale100",
  "withBackground",
  "variant",
];

export default ProgressBar;
